import { useCallback, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import {
  applyTheme,
  getSystemTheme,
  readStoredPreference,
  storePreference,
  subscribeToSystemTheme,
  type Theme,
  type ThemePreference,
} from '@/shared/lib/theme';
import { ThemeContext, type ThemeContextValue } from './theme-context';

export interface ThemeProviderProps {
  children: ReactNode;
}

/** Motyw systemowy przy renderze na serwerze — tam nie ma `matchMedia`. */
const getServerTheme = (): Theme => 'light';

/**
 * Zarzadza motywem jasnym/ciemnym.
 *
 * Pierwsze ustawienie atrybutu `data-theme` robi maly skrypt w `index.html` —
 * dzieki temu strona nie mruga jasnym tlem, zanim wystartuje React. Ten
 * komponent tylko przejmuje stan i utrzymuje go dalej.
 *
 * Motyw jest WYLICZANY w trakcie renderu z ustawienia uzytkownika i preferencji
 * systemu, a nie trzymany w osobnym stanie — inaczej kazda zmiana wymagalaby
 * dodatkowego przebiegu renderowania.
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readStoredPreference());

  const systemTheme = useSyncExternalStore(subscribeToSystemTheme, getSystemTheme, getServerTheme);
  const theme: Theme = preference === 'system' ? systemTheme : preference;

  // Jedyne zadanie efektu: zsynchronizowac DOM z wyliczonym motywem.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setPreference = useCallback((next: ThemePreference) => {
    storePreference(next);
    setPreferenceState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setPreference(theme === 'dark' ? 'light' : 'dark');
  }, [setPreference, theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, theme, setPreference, toggleTheme }),
    [preference, theme, setPreference, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
